'use client'

import { useMemo, type ComponentProps, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { TriangleAlertIcon } from 'lucide-react'

import { cn } from '@/shared/lib/cn'
import { Label } from '@/shared/ui/base/label'
import { Separator } from '@/shared/ui/base/separator'

function FieldSet({ className, ...props }: ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        'flex flex-col gap-4',
        'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
        className
      )}
      {...props}
    />
  )
}

/** Начертание подписи поля: размер и насыщенность берутся из общих типографских переменных. */
const labelTypeClass =
  '[font-size:var(--type-label-font-size)] [font-weight:var(--type-label-font-weight)] [line-height:var(--type-label-line-height)]'

/**
 * Заголовок подгруппы внутри набора полей: мельче подписи поля, чтобы уровни вложенности
 * различались на глаз. Общий для `FieldLegend` и `FieldLabel` — имя группе даёт либо рамка с
 * заголовком, либо видимая подпись.
 *
 * Цвет — отдельный токен `--section-foreground`, а не общий приглушённый: при 13 px
 * `--muted-foreground` даёт 4,49:1 на белом и ещё меньше на подложке `FieldPanel` — ниже порога
 * 4,5:1, который проект проверяет для мелкого текста. #5b616e держит 6,2:1 и при этом читается
 * как служебная надпись, а не как подпись поля.
 */
const sectionTypeClass =
  'text-section-foreground uppercase tracking-wide [font-size:var(--type-eyebrow-font-size)] [font-weight:var(--type-eyebrow-font-weight)] [line-height:var(--type-eyebrow-line-height)]'

const fieldLegendVariants = cva(
  // Заголовок рамки стоит вне потока flex-раскладки, поэтому gap набора полей до него не
  // достаёт и отступ снизу задаёт он сам. Пояснение под заголовком часть этого отступа
  // выбирает обратно своим отрицательным margin — так оно читается вместе с заголовком.
  'mb-3',
  {
    variants: {
      variant: {
        legend: labelTypeClass,
        label: labelTypeClass,
        // Заголовок целого смыслового блока внутри шага: одного уровня с заголовками разделов
        // страницы, поэтому и начертание у него их, а не подписи поля.
        block:
          '[font-size:var(--type-block-title-font-size)] [font-weight:var(--type-block-title-font-weight)] [line-height:var(--type-block-title-line-height)]',
        section: sectionTypeClass,
        // Черта нужна там, где группы идут подряд: без неё соседние группы сливаются в один список.
        'section-divided': `${sectionTypeClass} w-full border-b pb-2`,
      },
    },
    defaultVariants: {
      variant: 'legend',
    },
  }
)

function FieldLegend({
  className,
  variant = 'legend',
  ...props
}: ComponentProps<'legend'> & VariantProps<typeof fieldLegendVariants>) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(fieldLegendVariants({ variant }), className)}
      {...props}
    />
  )
}

/**
 * Поднабор полей на отдельной подложке: настройки, раскрытые под выбранным вариантом, и смысловые
 * группы полей. Отделяет уровень вложенности заметнее, чем отступ слева.
 *
 * Вид `muted` — серая подложка первого уровня вложенности. `card` — белая подложка для группы
 * внутри такой панели: две серые подложки подряд слились бы в одно пятно.
 */
const fieldPanelVariants = cva('flex w-full flex-col gap-4 rounded-xl border', {
  variants: {
    variant: {
      // Внешняя панель просторнее внутренней: так вложенность видна по одному только воздуху.
      muted: 'bg-muted/60 p-4 sm:p-6',
      card: 'bg-card p-4 sm:p-5',
    },
  },
  defaultVariants: {
    variant: 'muted',
  },
})

function FieldPanel({ className, variant, ...props }: ComponentProps<'div'> & VariantProps<typeof fieldPanelVariants>) {
  return <div data-slot="field-panel" className={cn(fieldPanelVariants({ variant }), className)} {...props} />
}

function FieldGroup({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        'group/field-group @container/field-group flex w-full flex-col gap-4 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4',
        className
      )}
      {...props}
    />
  )
}

const fieldVariants = cva('group/field flex w-full gap-3 data-[invalid=true]:text-destructive', {
  variants: {
    orientation: {
      vertical: ['flex-col [&>*]:w-full [&>.sr-only]:w-auto'],
      horizontal: [
        'flex-row items-center',
        '[&>[data-slot=field-label]]:flex-auto',
        'has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
      ],
      responsive: [
        'flex-col [&>*]:w-full [&>.sr-only]:w-auto @md/field-group:flex-row @md/field-group:items-center @md/field-group:[&>*]:w-auto',
        '@md/field-group:[&>[data-slot=field-label]]:flex-auto',
        '@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
      ],
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
})

function Field({
  className,
  orientation = 'vertical',
  ...props
}: ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  )
}

function FieldContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-content"
      className={cn('group/field-content flex flex-1 flex-col gap-1.5', className)}
      {...props}
    />
  )
}

const fieldLabelVariants = cva(
  [
    'group/field-label peer/field-label flex w-fit gap-2 group-data-[disabled=true]/field:opacity-50',
    // Ярлык с полем внутри — это кликабельный блок целиком, поэтому и курсор над ним «рука».
    // Собственный белый фон обязателен: такие карточки часто лежат на приглушённой подложке
    // `FieldPanel`, и без него они сливались бы с ней в одно серое пятно.
    'has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:cursor-pointer has-[>[data-slot=field]]:rounded-xl has-[>[data-slot=field]]:border has-[>[data-slot=field]]:bg-card [&>*]:data-[slot=field]:p-4',
    // Кольцо поверх рамки удваивает её толщину: выбранная карточка должна читаться с одного
    // взгляда, а одного пикселя синего на светлой заливке для этого мало.
    'has-data-[checked]:bg-primary/5 has-data-[checked]:border-primary has-data-[checked]:ring-1 has-data-[checked]:ring-primary',
  ],
  {
    variants: {
      variant: {
        label: '',
        section: sectionTypeClass,
      },
    },
    defaultVariants: {
      variant: 'label',
    },
  }
)

function FieldLabel({
  className,
  variant = 'label',
  ...props
}: ComponentProps<typeof Label> & VariantProps<typeof fieldLabelVariants>) {
  return (
    <Label
      data-slot="field-label"
      data-variant={variant}
      className={cn(fieldLabelVariants({ variant }), className)}
      {...props}
    />
  )
}

function FieldTitle({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        'flex w-fit items-center gap-2 [font-size:var(--type-label-font-size)] [font-weight:var(--type-label-font-weight)] [line-height:var(--type-label-line-height)] group-data-[disabled=true]/field:opacity-50',
        className
      )}
      {...props}
    />
  )
}

function FieldDescription({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        'text-muted-foreground [font-size:var(--type-supporting-font-size)] [font-weight:var(--type-supporting-font-weight)] [line-height:var(--type-supporting-line-height)] group-has-[[data-orientation=horizontal]]/field:text-balance',
        // Описание под заголовком секции держится вплотную к нему, а не к тому, что идёт ниже.
        'last:mt-0 nth-last-2:-mt-1 [[data-variant=legend]+&]:-mt-1.5 [[data-variant=section]+&]:-mt-1.5',
        '[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4',
        className
      )}
      {...props}
    />
  )
}

function FieldSeparator({
  children,
  className,
  ...props
}: ComponentProps<'div'> & {
  children?: ReactNode
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(
        'relative -my-2 h-5 [font-size:var(--type-supporting-font-size)] [font-weight:var(--type-supporting-font-weight)] [line-height:var(--type-supporting-line-height)] group-data-[variant=outline]/field-group:-mb-2',
        className
      )}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="bg-background text-muted-foreground relative mx-auto block w-fit px-2"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  )
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: ComponentProps<'div'> & {
  errors?: Array<{ message?: string } | undefined>
}) {
  const content = useMemo(() => {
    if (children) {
      return children
    }

    if (!errors) {
      return null
    }

    if (errors?.length === 1 && errors[0]?.message) {
      return errors[0].message
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {errors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
      </ul>
    )
  }, [children, errors])

  if (!content) {
    return null
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn(
        'text-destructive flex items-start gap-1.5 [font-size:var(--type-caption-font-size)] [font-weight:var(--type-caption-font-weight)] [line-height:var(--type-caption-line-height)]',
        className
      )}
      {...props}
    >
      {/* Значок повторяет смысл цвета: на красный текст полагаться нельзя — его различают не все. */}
      <TriangleAlertIcon aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <span>{content}</span>
    </div>
  )
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLegend,
  FieldPanel,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
}
